import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { V2apiClient } from '../../ApiClient';
import { Panel } from '../../ui/Panel';
import { Text } from '../../ui/Text';
import { Button } from '../../ui/Button';
import { PageBackground, SUIT_GLYPHS, suitColor } from '../../ui/PageBackground';
import { token } from '../../ui/theme';

interface Props {
  setPlayerCallback: (player: { id: string; name: string }) => void;
}

export const Home: React.FC<Props> = ({ setPlayerCallback }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Invite links send unauthenticated users here with ?redirect=/join/<code> so
  // we can return them to the invite once they've registered.
  const redirectTo = searchParams.get('redirect') || '/lobby';

  const register = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(undefined);
    try {
      const res = await V2apiClient.register(trimmed);
      if (res.status !== 200) {
        setError(res.body.error || 'Something went wrong');
        return;
      }
      setPlayerCallback(res.body);
      navigate(redirectTo);
    } catch {
      setError('Connection error. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageBackground>
      <Panel
        style={{
          position: 'relative',
          zIndex: 1,
          width: '340px',
          padding: '48px 60px',
          textAlign: 'center',
        }}
      >
        {/* Suit accent row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '18px',
            marginBottom: 'var(--space-6)',
          }}
        >
          {SUIT_GLYPHS.map((symbol, i) => (
            <span
              key={symbol}
              style={{
                fontSize: '26px',
                lineHeight: 1,
                color: suitColor(i),
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
              }}
            >
              {symbol}
            </span>
          ))}
        </div>

        <Text as="h1" variant="title" style={{ letterSpacing: '10px', fontWeight: 800 }}>
          SETBACK
        </Text>
        <Text as="p" variant="label" style={{ marginTop: 'var(--space-2)', letterSpacing: '4px' }}>
          Online Multiplayer
        </Text>

        <div style={divider} />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
          }}
        >
          <input
            style={{
              background: token('color-bg'),
              border: `1px solid ${focused ? token('color-gold') : token('color-border')}`,
              borderRadius: 'var(--radius-md)',
              padding: '14px 18px',
              color: token('color-text'),
              fontSize: 'var(--font-size-sm)',
              outline: 'none',
              transition: 'border-color var(--dur-fast) var(--ease-out)',
              fontFamily: 'inherit',
            }}
            type="text"
            placeholder="Enter your name"
            value={name}
            maxLength={20}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && register()}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
          {error && (
            <Text variant="muted" color={token('color-lose')}>
              {error}
            </Text>
          )}
          <Button variant="primary" size="lg" onClick={register} disabled={!name.trim() || loading}>
            {loading ? 'Joining…' : 'Enter the Lobby'}
          </Button>
        </div>
      </Panel>
    </PageBackground>
  );
};

const divider: React.CSSProperties = {
  height: '1px',
  background: 'linear-gradient(to right, transparent, var(--color-border), transparent)',
  margin: 'var(--space-6) 0',
};
